import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import { INCOME_TYPE, EXPENSE_TYPE, type Category } from "../../common/commonConstant";
import { buildCategoryLargeKey, type MasterItem } from "../../common/MasterContext";
import {
  buildExpenseStatusKey,
  MAX_DISPLAY_COUNT,
  type ExpenseStatusEntry,
} from "../../common/expenseStatus";
import { type MasterFormValues } from "./bop030w010";

interface BOP030W010TemplateProps {
  items: MasterItem[];
  expandedItems: Set<string>;
  isFormOpen: boolean;
  formValues: MasterFormValues;
  isLoading: boolean;
  expenseStatusMap: Map<string, ExpenseStatusEntry>;
  targetDrafts: Map<string, string>;
  isTargetSaving: boolean;
  displayedCount: number;
  onToggleExpand: (categoryLarge: string) => void;
  onBulkExpand: (keys: string[], expand: boolean) => void;
  onOpenAddForm: () => void;
  onOpenEditForm: (item: MasterItem) => void;
  onCancelForm: () => void;
  onFormTypeChange: (type: Category) => void;
  onFormCategoryLargeChange: (value: string) => void;
  onFormCategorySmallChange: (value: string) => void;
  onFormSubmit: () => void;
  onDelete: (item: MasterItem) => void;
  onTargetInputChange: (key: string, value: string) => void;
  onSaveTarget: (item: MasterItem, key: string) => void;
  onToggleDisplay: (item: MasterItem, checked: boolean) => void;
}

// 目標限度額に対する当月支出の割合に応じた行の強調クラスを返す（60%/80%/100%超）
const getLimitClass = (status: ExpenseStatusEntry | undefined): string => {
  if (!status || status.targetAmount === null || status.targetAmount <= 0) {
    return "";
  }
  const ratio = status.currentMonthAmount / status.targetAmount;
  if (ratio > 1) return "row-limit-over-100";
  if (ratio > 0.8) return "row-limit-warn-80";
  if (ratio > 0.6) return "row-limit-warn-60";
  return "";
};

const BOP030W010Template = ({
  items,
  expandedItems,
  isFormOpen,
  formValues,
  isLoading,
  expenseStatusMap,
  targetDrafts,
  isTargetSaving,
  displayedCount,
  onToggleExpand,
  onBulkExpand,
  onOpenAddForm,
  onOpenEditForm,
  onCancelForm,
  onFormTypeChange,
  onFormCategoryLargeChange,
  onFormCategorySmallChange,
  onFormSubmit,
  onDelete,
  onTargetInputChange,
  onSaveTarget,
  onToggleDisplay,
}: BOP030W010TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();

  // 収支区分＋項目（大）でグルーピング（初出順を表示順とする）。
  // MasterContext.deriveCategoryListsと同じキー生成規則を使い、収支区分をまたいだ
  // 同名の項目（大）が誤って同一グループに混在しないようにする。
  const categoryLargeOrder: string[] = [];
  const groupedByLarge = new Map<string, MasterItem[]>();
  items.forEach((item) => {
    const key = buildCategoryLargeKey(item.hanyoMoji1 || "", item.hanyoMoji2 || "");
    if (!groupedByLarge.has(key)) {
      groupedByLarge.set(key, []);
      categoryLargeOrder.push(key);
    }
    groupedByLarge.get(key)?.push(item);
  });

  const incomeGroupKeys = categoryLargeOrder.filter(
    (key) => (groupedByLarge.get(key) || [])[0]?.hanyoMoji1 === INCOME_TYPE,
  );
  const expenseGroupKeys = categoryLargeOrder.filter(
    (key) => (groupedByLarge.get(key) || [])[0]?.hanyoMoji1 === EXPENSE_TYPE,
  );
  const isAllExpanded =
    categoryLargeOrder.length > 0 && categoryLargeOrder.every((key) => expandedItems.has(key));
  const isIncomeExpanded =
    incomeGroupKeys.length > 0 && incomeGroupKeys.every((key) => expandedItems.has(key));
  const isExpenseExpanded =
    expenseGroupKeys.length > 0 && expenseGroupKeys.every((key) => expandedItems.has(key));

  return (
    <div
      className={`bop030w010-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      {(isLoading || isTargetSaving) && (
        <div className="bop030w010-loading-overlay">
          <Spinner label="処理中..." />
        </div>
      )}

      <div className="bop030w010-content">
        <h1 className="screen-title">項目管理</h1>

        <div className="category-section">
          <div className="section-header">
            <h2 className="section-title">項目一覧</h2>
            <button type="button" className="add-button" onClick={onOpenAddForm}>
              ＋ 新規追加
            </button>
          </div>

          <div className="bulk-expand-row">
            <button
              type="button"
              className="bulk-expand-button"
              onClick={() => onBulkExpand(categoryLargeOrder, !isAllExpanded)}
            >
              {isAllExpanded ? "全項目を折りたたむ" : "全項目を展開"}
            </button>
            <button
              type="button"
              className="bulk-expand-button"
              onClick={() => onBulkExpand(incomeGroupKeys, !isIncomeExpanded)}
              disabled={incomeGroupKeys.length === 0}
            >
              {isIncomeExpanded ? "収入項目をすべて折りたたむ" : "収入項目をすべて展開"}
            </button>
            <button
              type="button"
              className="bulk-expand-button"
              onClick={() => onBulkExpand(expenseGroupKeys, !isExpenseExpanded)}
              disabled={expenseGroupKeys.length === 0}
            >
              {isExpenseExpanded ? "支出項目をすべて折りたたむ" : "支出項目をすべて展開"}
            </button>
          </div>

          <div className="category-table">
            <div className="table-header">
              <div className="col-type">分類</div>
              <div className="col-label">項目</div>
              <div className="col-actions"></div>
            </div>

            {categoryLargeOrder.map((groupKey) => {
              const rows = groupedByLarge.get(groupKey) || [];
              const isExpanded = expandedItems.has(groupKey);
              const type = rows[0]?.hanyoMoji1 || "";
              const large = rows[0]?.hanyoMoji2 || "";

              return (
                <div key={groupKey}>
                  {/* 項目（大）行（グルーピングの見出し。展開/折りたたみのみ） */}
                  <div
                    className="table-row large-item"
                    onClick={() => onToggleExpand(groupKey)}
                  >
                    <div className="col-type">{type}</div>
                    <div className="col-label-with-icon">
                      <span
                        className={`toggle-icon ${isExpanded ? "expanded" : ""}`}
                      >
                        ▶
                      </span>
                      <span className="col-label-text" title={large}>
                        {large}
                      </span>
                    </div>
                    <div className="col-actions"></div>
                  </div>

                  {/* 項目（小）行 - 展開時のみ表示。編集・削除の対象単位。 */}
                  {isExpanded &&
                    rows.map((item) => {
                      const isExpense = item.hanyoMoji1 === EXPENSE_TYPE;
                      const statusKey = buildExpenseStatusKey(
                        item.hanyoMoji1 || "",
                        item.hanyoMoji2 || "",
                        item.hanyoMoji3 || "",
                      );
                      const status = isExpense ? expenseStatusMap.get(statusKey) : undefined;
                      const limitClass = getLimitClass(status);
                      const committedValue =
                        status?.targetAmount !== null && status?.targetAmount !== undefined
                          ? String(status.targetAmount)
                          : "";
                      const inputValue = targetDrafts.get(statusKey) ?? committedValue;
                      const isDirty = inputValue !== committedValue;
                      const isDisplayed = status?.displayFlg === 1;
                      // 目標限度額が未設定、または上限件数に達している場合はONにできない
                      const isDisplayDisabled =
                        isTargetSaving ||
                        (!isDisplayed &&
                          (committedValue === "" || displayedCount >= MAX_DISPLAY_COUNT));

                      return (
                        <div
                          key={item.hanyoKoumokuCd}
                          className={`table-row small-item ${limitClass}`}
                        >
                          <div className="col-type">{item.hanyoMoji1}</div>
                          <div className="col-label" title={item.hanyoMoji3 || undefined}>
                            {item.hanyoMoji3}
                          </div>
                          <div className="col-actions">
                            {isExpense ? (
                              <div className="target-limit-group">
                                <input
                                  type="number"
                                  className="target-limit-input"
                                  min={0}
                                  value={inputValue}
                                  placeholder="目標限度額"
                                  aria-label={`${item.hanyoMoji3}の目標限度額`}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => onTargetInputChange(statusKey, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && isDirty) {
                                      e.stopPropagation();
                                      onSaveTarget(item, statusKey);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="target-save-button"
                                  disabled={!isDirty}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSaveTarget(item, statusKey);
                                  }}
                                >
                                  設定
                                </button>
                                <label
                                  className={`display-flg-label ${isDisplayDisabled ? "disabled" : ""}`}
                                  title={
                                    isDisplayed
                                      ? undefined
                                      : committedValue === ""
                                        ? "目標限度額を設定すると選択できます"
                                        : displayedCount >= MAX_DISPLAY_COUNT
                                          ? `表示できるのは最大${MAX_DISPLAY_COUNT}件です（他の項目を外してください）`
                                          : undefined
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isDisplayed}
                                    disabled={isDisplayDisabled}
                                    aria-label={`${item.hanyoMoji3}を収支登録画面に表示`}
                                    onChange={(e) => onToggleDisplay(item, e.target.checked)}
                                  />
                                  登録画面に表示
                                </label>
                              </div>
                            ) : (
                              <div className="target-limit-group" />
                            )}
                            <div className="row-edit-actions">
                              <button
                                type="button"
                                className="edit-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenEditForm(item);
                                }}
                              >
                                編集
                              </button>
                              <button
                                type="button"
                                className="row-delete-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(item);
                                }}
                              >
                                削除
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })}
          </div>
        </div>

        {isFormOpen && (
          <div
            className="master-form-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={formValues.hanyoKoumokuCd ? "項目の編集" : "項目の追加"}
          >
            <div className="master-form-panel">
              <h2 className="section-title">
                {formValues.hanyoKoumokuCd ? "項目の編集" : "項目の追加"}
              </h2>

              <div className="form-row">
                <label className="form-label required-label">収支</label>
                <div className="radio-group">
                  <label className="radio-item">
                    <input
                      type="radio"
                      name="masterFormType"
                      checked={formValues.type === INCOME_TYPE}
                      onChange={() => onFormTypeChange(INCOME_TYPE)}
                    />
                    収入
                  </label>
                  <label className="radio-item">
                    <input
                      type="radio"
                      name="masterFormType"
                      checked={formValues.type === EXPENSE_TYPE}
                      onChange={() => onFormTypeChange(EXPENSE_TYPE)}
                    />
                    支出
                  </label>
                </div>
              </div>

              <div className="form-row">
                <label className="form-label required-label" htmlFor="masterFormCategoryLarge">
                  項目（大）
                </label>
                <input
                  id="masterFormCategoryLarge"
                  type="text"
                  value={formValues.categoryLarge}
                  maxLength={255}
                  onChange={(e) => onFormCategoryLargeChange(e.target.value)}
                />
              </div>

              <div className="form-row">
                <label className="form-label required-label" htmlFor="masterFormCategorySmall">
                  項目（小）
                </label>
                <input
                  id="masterFormCategorySmall"
                  type="text"
                  value={formValues.categorySmall}
                  maxLength={255}
                  onChange={(e) => onFormCategorySmallChange(e.target.value)}
                />
              </div>

              <div className="master-form-actions">
                <button type="button" className="clear-button" onClick={onCancelForm}>
                  キャンセル
                </button>
                <button type="button" className="register-button" onClick={onFormSubmit}>
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BOP030W010Template;
